import {
  AxiosDefaults,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  HeadersDefaults
} from 'axios';
import { get, isEmpty, merge, omit, pick } from 'lodash';
import { normalize, Schema } from 'normalizr';
// Use direct imports instead of the app-core barrel to avoid pulling in
// setup/authenticator and setup/signer (which have top-level code that
// crashes when AWS deps are stubbed as `false` in the MFE webpack config).
import { AnyObject } from '../app-core/types';
import { auth0Client } from '../app-core/setup/auth0-client';
import { Auth0ClientOptions } from '@auth0/auth0-spa-js';
import { v4 as uuid } from 'uuid';

import { AmplifyAuthConfig } from '../authenticator/amplify/types';
import { Authenticator } from '../authenticator/authenticator';
import { NormalisedData } from '../redux-helpers/types';
import { AWSRequestConfig } from '../signer/aws/aws-request-config';
import { AWS4SignerConfig } from '../signer/aws/types';
import { Signer } from '../signer/signer';
import { Credentials, RequestConfig } from '../signer/types';
import { AnyData, APIRequestConfig } from './types';
// Use direct import to avoid the predict barrel (which transitively imports
// the organisations barrel, pulling in heavy UI component deps).
import { matchAnyReportingInternalRenderReportUrl } from '../predict/utils/reporting-routes-utils';
import { matchOrgDetailPath } from '../organisations/utils/routes-utils';
import { Auth0Credentials } from '../authenticator/auth0/types';
import { Auth0SignerConfig } from '../app-core/setup/auth0-signer';
// Use direct import to avoid the authenticator barrel (which re-exports Hub
// from aws-amplify/utils and other AWS-related modules).
import { AmplifyAuthenticator } from '../authenticator/amplify/amplify-authenticator';

export const INTERNAL_USER_ID_QUERY_PARAM = 'userId';

const PREDICT_API_URL = window.__env__.PREDICT_API_URL;

const WSC_STAGING_INTERNAL_URL = 'https://wsc.core.staging.futrli.net/';
const WSC_PRODUCTION_INTERNAL_URL = 'https://wsc.core.prod.futrli.net/';

const PREDICT_STAGING_INTERNAL_URL = 'https://predict.core.staging.futrli.net/';
const PREDICT_PRODUCTION_INTERNAL_URL = 'https://predict.core.prod.futrli.net/';

/**
 * @template SC AWS4SignerConfig | ...
 * @template RC RC extends RequestConfig = AWSRequestConfig | ...
 * @template AC AmplifyAuthConfig | ...
 */
export class APIClient<
  SC = AWS4SignerConfig | Auth0SignerConfig,
  RC extends RequestConfig = AWSRequestConfig,
  AC = AmplifyAuthConfig | Auth0ClientOptions
> {
  constructor(
    private client: AxiosInstance,
    private signer?: Signer<SC, RC, Credentials | Auth0Credentials>,
    private authenticator?: Authenticator<AC>
  ) {}

  /**
   * Makes an async request to the specific endpoint.
   * @param {APIRequestConfig} apiRequestConfig
   * @returns {Promise<AxiosResponse<T>>}
   * @throws {AxiosError}
   * @template T response "data" type
   */
  // eslint-disable-next-line complexity
  public async makeRequest<T = AnyData>(apiRequestConfig: APIRequestConfig): Promise<AxiosResponse<T> | never> {
    const { payload, meta, validateStatus, sxp } = apiRequestConfig;

    let requestConfig: AxiosRequestConfig = this.mergeRequestConfig(this.client.defaults, payload.request);

    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    const userId = (searchParams.get(INTERNAL_USER_ID_QUERY_PARAM) || '').trim();

    const isInternalReportPage = matchAnyReportingInternalRenderReportUrl(url.pathname);

    // Don't sign requests if it is internal render report page
    if (this.signer && this.authenticator && !isInternalReportPage) {
      requestConfig = (await this.signRequest(requestConfig)) as AxiosRequestConfig;
    }

    // Add headers and baseURL if this is internal render report page
    if (isInternalReportPage && userId) {
      requestConfig = this.getInternalHeaders(requestConfig, userId);
      requestConfig = this.getInternalBaseUrl(requestConfig);
    }

    let requestObject = this.cleanHeaders(requestConfig);
    if (validateStatus) {
      requestObject = { ...requestObject, validateStatus };
    }

    if (sxp) {
      requestObject.headers = {
        ...requestObject.headers,
        'Authorization': `${await auth0Client.getTokenSilently()}`,
        'Content-Type': 'application/json',
        'x-mfe-origin': 'sbc.core.scene.ui',
        'x-use-msw': 'true',
        'x-idempotency-key': uuid()
      };
      requestObject.data = requestObject.data || null;
    }

    const response: AxiosResponse = await this.client.request(requestObject);

    if (meta && !isEmpty(meta.schema)) {
      const normalisedData = this.normaliseData(response.data, meta.schema);

      response.data = normalisedData;
    }

    return response;
  }

  /**
   * Makes an async `public (not authenticated)` request to the specific endpoint.
   * @param {APIRequestConfig} apiRequestConfig
   * @returns {Promise<AxiosResponse<T>>}
   * @throws {AxiosError}
   * @template T response "data" type
   */
  public async makePublicRequest<T = AnyData>(apiRequestConfig: APIRequestConfig): Promise<AxiosResponse<T> | never> {
    const { payload, meta } = apiRequestConfig;

    const requestConfig: AxiosRequestConfig = this.mergeRequestConfig(this.client.defaults, payload.request);

    const response: AxiosResponse = await this.client.request(this.cleanHeaders(requestConfig));

    if (meta && !isEmpty(meta.schema)) {
      const normalisedData = this.normaliseData(response.data, meta.schema);

      response.data = normalisedData;
    }

    return response;
  }

  /**
   * Clean request config headers that should not be provided to the axios client.
   *
   * @param {AxiosRequestConfig} config
   * @returns {AxiosRequestConfig}
   */
  public cleanHeaders(config: AxiosRequestConfig): AxiosRequestConfig {
    return Object.assign({}, config, { headers: omit(config.headers, ['Content-Length']) });
  }

  /**
   * Will return a new config with the baseURL config option set correctly for an internal report
   * request
   * @param requestConfig
   */
  public getInternalBaseUrl(requestConfig: AxiosRequestConfig): AxiosRequestConfig {
    const isStaging = PREDICT_API_URL.indexOf('staging') !== -1;
    let optionalBaseUrl = PREDICT_API_URL || undefined;

    if (requestConfig.url && matchOrgDetailPath(requestConfig.url, true)) {
      optionalBaseUrl = isStaging ? WSC_STAGING_INTERNAL_URL : WSC_PRODUCTION_INTERNAL_URL;
    } else {
      optionalBaseUrl = isStaging ? PREDICT_STAGING_INTERNAL_URL : PREDICT_PRODUCTION_INTERNAL_URL;
    }

    return {
      ...requestConfig,
      baseURL: optionalBaseUrl
    };
  }

  /**
   * Will return a new config with the headers config option set correctly for an internal report
   * request
   * @param requestConfig
   * @param userId
   */
  public getInternalHeaders(requestConfig: AxiosRequestConfig, userId: string) {
    return {
      ...requestConfig,
      headers: {
        'X-COGNITO-USERNAME': userId,
        'Authorization': userId
      }
    };
  }

  /**
   * Merges the request configuration from the action payload with the default axios client.
   * @param {AxiosRequestConfig} clientConfig
   * @param {AxiosRequestConfig} requestConfig
   * @returns {AxiosRequestConfig}
   */
  private mergeRequestConfig(
    clientConfig: Omit<AxiosDefaults, 'headers'> & {
      headers: HeadersDefaults & {
        [key: string]: AxiosHeaderValue;
      };
    },
    requestConfig: AxiosRequestConfig
  ): AxiosRequestConfig {
    const defaultConfig = pick(clientConfig, ['baseURL']);
    // If 'data' exists, ensure 'Content-Type' is set to 'application/json'
    if (requestConfig.data) {
      requestConfig.headers = {
        ...(requestConfig.headers || {}),
        'Content-Type': 'application/json'
      };
    }
    return merge({}, defaultConfig, requestConfig);
  }

  /**
   * Signs a request in order to make it stable for making a request to the API Gateway.
   * @param {AxiosRequestConfig} requestConfig
   * @returns {Promise<RC>} signed request
   */
  private async signRequest(requestConfig: AxiosRequestConfig): Promise<RC | never> {
    if (!this.signer || !this.authenticator) {
      throw new Error('A Signer and a Authenticator needs to be provided for signing the request');
    }

    const creds: Credentials | Auth0Credentials = (await this.authenticator.fetchCredentials()) as
      | Credentials
      | Auth0Credentials;

    const signedRequestConfig = this.signer.sign(requestConfig, creds);

    if (this.authenticator instanceof AmplifyAuthenticator) {
      signedRequestConfig.cleanHeaders();
      return signedRequestConfig.getConfig() as RC;
    }

    return signedRequestConfig;
  }

  /**
   * Normalise the data with the schema to save it into the Redux state.
   *
   * @function normaliseData
   * @param {AnyObject | unknown[]} data
   * @param {Schema} schema
   * @return {NormalisedData<unknown>}
   */
  private normaliseData(data: AnyObject | unknown[], schema: Schema): NormalisedData<unknown> {
    const getData = get(data, 'data', data);

    return normalize(getData, schema);
  }
}
